import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNewFeedbackStream } from '../../hooks/useNewFeedbackStream';
import { formatDateString } from '../../utils/dateFormat';

const Stars = ({ n }) => {
  const filled = Math.max(0, Math.min(5, n));
  return (
    <span className="lf-toast-stars" aria-label={`${filled} of 5`}>
      {Array.from({ length: 5 }, (_, i) =>
        i < filled ? <span key={i}>★</span> : <span key={i} className="off">★</span>,
      )}
    </span>
  );
};

/**
 * Toasts only for new submissions on calendar-today (operational monitoring).
 */
export default function LiveFeedbackToast({ holdMs = 6000, maxVisible = 3 }) {
  const navigate = useNavigate();
  const todayStr = formatDateString(new Date());
  const { latest } = useNewFeedbackStream({
    size: 5,
    dateString: todayStr,
    pulseOnArrival: true,
  });
  const [stack, setStack] = useState([]);
  const keyRef = useRef(0);

  useEffect(() => {
    if (!latest) return;
    const key = ++keyRef.current;
    setStack((prev) => [{ key, item: latest, leaving: false }, ...prev].slice(0, maxVisible));
    const leaveT = setTimeout(() => {
      setStack((prev) => prev.map((s) => (s.key === key ? { ...s, leaving: true } : s)));
    }, holdMs);
    const removeT = setTimeout(() => {
      setStack((prev) => prev.filter((s) => s.key !== key));
    }, holdMs + 240);
    return () => { clearTimeout(leaveT); clearTimeout(removeT); };
  }, [latest, holdMs, maxVisible]);

  if (!stack.length) return null;
  return (
    <div className="lf-toast-stack" aria-live="polite">
      {stack.map(({ key, item, leaving }) => (
        <div
          key={key}
          className={`lf-toast${leaving ? ' is-leaving' : ''}`}
          role="status"
          onClick={() => navigate('/dashboard/feedback')}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/feedback'); }}
          tabIndex={0}
        >
          <Stars n={item.stars} />
          <div className="lf-toast-text">
            <b>New feedback</b> · {item.supervisorName}
            <span className="lf-toast-sub">{item.shopName} · {item.relative}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
